import React from 'react';
import { trackEvent } from 'src/services/analytics';
import {
	useCurrentRefinements,
	useClearRefinements,
	useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { InputTagCollapsingList } from '@src/components/InputTagList/InputTagCollapsingList';
import { AlgoliaFacets } from '@src/clients/Algolia/constants';
import { formatMoney } from '@src/utilities/numberFormattingUtility';
import {
	useSearchContext,
	useSearchDispatch,
} from '@src/components/Providers/SearchProvider/SearchProvider';

export const checkLabelText = (item, refinement): string => {
	switch (item.attribute) {
		case 'fundingLevel':
			return refinement.value === '\\-1'
				? '100% tuition paid'
				: `Up to ${formatMoney(refinement.value)} tuition paid per year`;
		case 'completionTimeRange':
			return `Time to complete: ${refinement.value}`;
		case 'weeklyEffortRange':
			return `Effort: ${refinement.value}`;
		case AlgoliaFacets['High school GPA']:
			return `Min HS GPA ${refinement.label}`;
		case AlgoliaFacets['College GPA']:
			return `Min College GPA ${refinement.label}`;
		case AlgoliaFacets['College credits']:
			return `Min College Credits ${refinement.label}`;
		case 'isAcceptingApplications':
			return refinement.value === 'true' ? 'Open programs' : '';
		default:
			return handleDefault(refinement);
	}
};

const getStartDateFilterLabel = (value: string): string => {
	switch (value) {
		case 'next_30_days':
			return 'Start in next 30 days';
		case 'next_60_days':
			return 'Start in next 60 days';
		case 'next_90_days':
			return 'Start in next 90 days';
		default:
			return '';
	}
};

const handleDefault = refinement => {
	if (refinement.label.includes('>')) {
		if (refinement.value.includes('Certificates & bootcamps >')) {
			return `${refinement.label.split('>')[1]} Certificate`;
		} else {
			return refinement.label.split('>')[1];
		}
	} else {
		return refinement.label;
	}
};

export const FacetsInputTagList: React.FC = () => {
	const { items, refine } = useCurrentRefinements();
	const { refine: clearRefinements } = useClearRefinements();
	const searchContext = useSearchContext();
	const searchDispatch = useSearchDispatch();

	// Handle cases where context might not be available (e.g., in tests)
	const startDateFilter = searchContext?.startDateFilter;
	const index = searchContext?.index;
	const dispatch = searchDispatch?.dispatch;

	// Try to get instantSearch, but handle cases where it's not available
	let uiState, setUiState;
	try {
		const instantSearch = useInstantSearch();
		uiState = instantSearch?.uiState;
		setUiState = instantSearch?.setUiState;
	} catch (error) {
		// useInstantSearch is not available in test environment
		uiState = null;
		setUiState = null;
	}

	const refinements = items.reduce((acc, item) => {
		item.refinements.forEach(refinement => {
			if (
				refinement.value !== 'Any time to complete' &&
				refinement.value !== 'Any hours per week'
			) {
				refinement.label = checkLabelText(item, refinement);
				if (refinement.label) {
					acc.push(refinement);
				}
			}
		});
		return acc;
	}, []);

	// Add start date filter tag if active
	if (startDateFilter && startDateFilter !== 'any_start_date') {
		const startDateLabel = getStartDateFilterLabel(startDateFilter);
		if (startDateLabel) {
			refinements.push({
				attribute: 'startDateFilter',
				label: startDateLabel,
				value: startDateFilter,
			});
		}
	}

	const removeAllInputTags = () => {
		clearRefinements();

		if (
			startDateFilter &&
			startDateFilter !== 'any_start_date' &&
			dispatch &&
			uiState &&
			setUiState &&
			index
		) {
			dispatch({
				type: 'set_start_date_filter',
				startDateFilter: 'any_start_date',
				startDateProgramUuids: [],
			});
		}

		trackEvent('FiltersCleared', {});
	};

	const removeInputTag = item => {
		if (item.attribute === 'startDateFilter') {
			if (dispatch && uiState && setUiState && index) {
				dispatch({
					type: 'set_start_date_filter',
					startDateFilter: 'any_start_date',
					startDateProgramUuids: [],
				});
			}

			trackEvent('FilterRemoved', {
				filterCategory: 'startDate',
				filterValue: [item.label],
			});
		} else {
			trackEvent('FilterRemoved', {
				filterCategory: item.attribute,
				filterValue: [item.label],
			});
			refine(item);
		}
	};

	return (
		<InputTagCollapsingList
			items={refinements}
			removeAll={removeAllInputTags}
			removeFunction={removeInputTag}
		/>
	);
};
